import { useState, useEffect, useRef, useCallback } from "react";
import { BRAND } from "../constants";

export default function AdminPage() {
  var devPassword = (window.__APP_CONFIG__ && window.__APP_CONFIG__.devAdminPassword) || "";
  var savedPassword = sessionStorage.getItem("admin_password") || "";
  var willAutoUnlock = !!devPassword || !!savedPassword;
  var [password, setPassword] = useState(devPassword || savedPassword);
  var [unlocked, setUnlocked] = useState(false);
  var [checking, setChecking] = useState(willAutoUnlock);
  var [authError, setAuthError] = useState("");

  // Live stats from API
  var [liveStats, setLiveStats] = useState(null);
  var [statsLoading, setStatsLoading] = useState(false);
  var [statsError, setStatsError] = useState("");

  // Publish manifest (snapshot history)
  var [manifest, setManifest] = useState(null);
  var [manifestLoading, setManifestLoading] = useState(false);

  // Sync state
  var [syncing, setSyncing] = useState(false);
  var [syncResult, setSyncResult] = useState(null);

  // Publish state
  var [publishing, setPublishing] = useState(null); // file being published
  var [publishResult, setPublishResult] = useState(null);

  // "Last checked" ticker
  var [lastCheckedAt, setLastCheckedAt] = useState(null);
  var [lastCheckedAgo, setLastCheckedAgo] = useState("");

  var handleUnlock = function () {
    setAuthError("");
    setStatsLoading(true);

    fetch("/api/admin-stats?password=" + encodeURIComponent(password))
      .then(function (res) {
        if (res.status === 401) throw new Error("Invalid password");
        if (!res.ok) throw new Error("Failed to fetch stats");
        return res.json();
      })
      .then(function (data) {
        setLiveStats(data);
        setLastCheckedAt(Date.now());
        setUnlocked(true);
        setStatsLoading(false);
        setChecking(false);
        sessionStorage.setItem("admin_password", password);
        // Load manifest after unlock
        loadManifest();
      })
      .catch(function (err) {
        setStatsLoading(false);
        setChecking(false);
        setAuthError(err.message || "Something went wrong");
        sessionStorage.removeItem("admin_password");
      });
  };

  var loadManifest = function () {
    setManifestLoading(true);
    fetch("/api/publish?action=manifest&password=" + encodeURIComponent(password))
      .then(function (res) {
        if (!res.ok) throw new Error("Failed to load manifest");
        return res.json();
      })
      .then(function (data) {
        setManifest(data);
        setManifestLoading(false);
      })
      .catch(function (err) {
        console.error("Manifest load failed:", err.message);
        setManifestLoading(false);
      });
  };

  // Auto-unlock on localhost or if password is saved
  useEffect(function () {
    if (devPassword || savedPassword) handleUnlock();
  }, []);

  var refreshStats = function () {
    setStatsLoading(true);
    setStatsError("");
    fetch("/api/admin-stats?password=" + encodeURIComponent(password))
      .then(function (res) {
        if (!res.ok) throw new Error("Failed to fetch stats");
        return res.json();
      })
      .then(function (data) {
        setLiveStats(data);
        setLastCheckedAt(Date.now());
        setStatsLoading(false);
      })
      .catch(function (err) {
        setStatsError(err.message);
        setStatsLoading(false);
      });
  };

  // Auto-refresh stats every 15s when unlocked
  var refreshTimerRef = useRef(null);
  useEffect(function () {
    if (!unlocked) return;
    refreshTimerRef.current = setInterval(function () {
      fetch("/api/admin-stats?password=" + encodeURIComponent(password))
        .then(function (res) {
          if (!res.ok) throw new Error("Failed");
          return res.json();
        })
        .then(function (data) {
          setLiveStats(data);
          setLastCheckedAt(Date.now());
        })
        .catch(function () {});
    }, 15000);
    return function () { clearInterval(refreshTimerRef.current); };
  }, [unlocked, password]);

  // Tick "last checked" display every second
  useEffect(function () {
    if (!lastCheckedAt) return;
    function tick() {
      var secs = Math.floor((Date.now() - lastCheckedAt) / 1000);
      if (secs < 5) setLastCheckedAgo("just now");
      else if (secs < 60) setLastCheckedAgo(secs + "s ago");
      else setLastCheckedAgo(Math.floor(secs / 60) + "m ago");
    }
    tick();
    var id = setInterval(tick, 1000);
    return function () { clearInterval(id); };
  }, [lastCheckedAt]);

  // Sync from Google Sheets
  var handleSync = function () {
    setSyncing(true);
    setSyncResult(null);
    setPublishResult(null);

    fetch("/api/publish", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "sync", password: password }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (d) { throw new Error(d.error || "Sync failed"); });
        return res.json();
      })
      .then(function (data) {
        setSyncing(false);
        setSyncResult(data);
        if (data.manifest) setManifest(data.manifest);
        // Refresh sheet stats
        refreshStats();
      })
      .catch(function (err) {
        setSyncing(false);
        setSyncResult({ error: err.message });
      });
  };

  // Publish a snapshot
  var handlePublish = function (file) {
    setPublishing(file);
    setPublishResult(null);

    fetch("/api/publish", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "publish", password: password, file: file }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (d) { throw new Error(d.error || "Publish failed"); });
        return res.json();
      })
      .then(function (data) {
        setPublishing(null);
        setPublishResult({ success: true, file: file });
        if (data.manifest) setManifest(data.manifest);
      })
      .catch(function (err) {
        setPublishing(null);
        setPublishResult({ error: err.message });
      });
  };

  var formatTimestamp = function (file) {
    // "tributes-20260308-143022" → "Mar 8, 2026 2:30 PM"
    var m = file.match(/tributes-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
    if (!m) return file;
    var d = new Date(m[1] + "-" + m[2] + "-" + m[3] + "T" + m[4] + ":" + m[5] + ":" + m[6]);
    if (isNaN(d.getTime())) return file;
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  var formatDate = function (iso) {
    if (!iso) return "Unknown";
    var d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  // Show blank screen while auto-verifying saved password
  if (checking) {
    return <div style={{ minHeight: "100vh", background: BRAND.warm }} />;
  }

  // Password gate
  if (!unlocked) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.warm, fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: "12px", padding: "32px", maxWidth: "360px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: BRAND.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={BRAND.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 800, color: BRAND.text, margin: 0 }}>Step Up Admin</h2>
            <p style={{ fontSize: "13px", color: BRAND.textLight, marginTop: "4px" }}>Enter the admin password to continue</p>
          </div>

          <input
            type="password"
            value={password}
            onChange={function (e) { setPassword(e.target.value); setAuthError(""); }}
            onKeyDown={function (e) { if (e.key === "Enter" && password) handleUnlock(); }}
            placeholder="Password"
            style={{
              width: "100%", padding: "10px 12px", border: "1.5px solid " + (authError ? "#D32F2F" : "#DDD"),
              borderRadius: "6px", fontSize: "14px", outline: "none", boxSizing: "border-box",
            }}
          />

          {authError && (
            <p style={{ color: "#D32F2F", fontSize: "12px", margin: "6px 0 0" }}>{authError}</p>
          )}

          <button
            onClick={password ? handleUnlock : undefined}
            disabled={!password || statsLoading}
            style={{
              width: "100%", marginTop: "12px", background: password && !statsLoading ? BRAND.primary : "#DDD",
              color: "#fff", border: "none", borderRadius: "6px", padding: "12px",
              fontSize: "14px", fontWeight: 600, cursor: password && !statsLoading ? "pointer" : "default",
            }}
          >
            {statsLoading ? "Checking..." : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  // Admin dashboard
  var sheetCounts = liveStats && liveStats.counts ? liveStats.counts : null;
  var hasPendingApprovals = sheetCounts && sheetCounts.pending > 0;
  var sheetUrl = liveStats && liveStats.sheetUrl ? liveStats.sheetUrl : null;
  var snapshots = manifest && manifest.snapshots ? manifest.snapshots : [];
  var liveFile = manifest ? manifest.live : null;

  // Check if there are approved tributes not yet synced to a snapshot
  var latestSnapshot = snapshots.length > 0 ? snapshots[0] : null;
  var latestSnapshotCount = latestSnapshot ? (latestSnapshot.tributeCount || 0) : 0;
  var hasUnsyncedApproved = sheetCounts && (
    snapshots.length === 0 || sheetCounts.approved !== latestSnapshotCount
  );

  return (
    <div style={{ minHeight: "100vh", background: BRAND.warm, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Purple branded header */}
      <div style={{
        background: BRAND.primary, padding: "24px 20px 20px", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.08,
          backgroundImage: "radial-gradient(circle, #fff 1.5px, transparent 1.5px)",
          backgroundSize: "20px 20px",
        }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: "600px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
              Decatur Education Foundation
            </div>
            <h1 style={{
              fontFamily: "'Playfair Display', serif", fontSize: "24px", fontWeight: 800,
              color: "#fff", margin: 0,
            }}>
              Step Up Admin
            </h1>
          </div>
          <a href="/" style={{
            fontSize: "12px", color: BRAND.primary, textDecoration: "none",
            background: "#fff", padding: "8px 16px", borderRadius: "20px",
            fontWeight: 600, boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}>
            View Tribute Wall
          </a>
        </div>
      </div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px 16px 32px" }}>

        {/* Google Sheet stats — big circles */}
        {sheetCounts && (
          <div style={{
            background: "#fff", borderRadius: "12px", padding: "24px 20px 20px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "16px",
          }}>
            <h3 style={{ fontSize: "13px", fontWeight: 600, color: BRAND.textLight, margin: "0 0 16px", textAlign: "center" }}>
              Google Sheet
            </h3>
            <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginBottom: "16px" }}>
              <StatCircle value={sheetCounts.approved} label="Approved" color="#2E7D32" bgColor="#E8F5E9" />
              <StatCircle value={sheetCounts.pending} label="Needs Review" color="#E65100" bgColor="#FFF3E0" highlight={sheetCounts.pending > 0} />
              <StatCircle value={sheetCounts.rejected} label="Rejected" color="#C62828" bgColor="#FFEBEE" />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", flexWrap: "wrap" }}>
              <p style={{ fontSize: "11px", color: "#999", margin: 0 }}>
                {statsLoading ? (
                  <>
                    Checking Google Sheet
                    <span style={{ marginLeft: "5px" }}>
                      <span style={{
                        display: "inline-block", width: "8px", height: "8px",
                        border: "1.5px solid #DDD", borderTopColor: "#999",
                        borderRadius: "50%", animation: "spin 0.6s linear infinite",
                        verticalAlign: "middle",
                      }} />
                    </span>
                  </>
                ) : (
                  <>
                    {"Checked " + lastCheckedAgo}
                    <span style={{ margin: "0 4px", color: "#DDD" }}>&middot;</span>
                  </>
                )}
              </p>
              {!statsLoading && (
                <button
                  onClick={refreshStats}
                  style={{
                    background: "none", border: "none", padding: 0,
                    fontSize: "11px", color: BRAND.primary, cursor: "pointer",
                    fontWeight: 600, fontFamily: "inherit",
                  }}
                >
                  Check now
                </button>
              )}
              {sheetUrl && (
                <>
                  <span style={{ color: "#DDD", fontSize: "11px" }}>&middot;</span>
                  <a href={sheetUrl} target="_blank" rel="noopener noreferrer" style={{
                    fontSize: "11px", color: BRAND.primary, fontWeight: 600,
                    textDecoration: "none",
                  }}>
                    Open Sheet
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        {/* Action cards */}
        {hasPendingApprovals && (
          <ActionCard
            icon="review"
            title={sheetCounts.pending === 1 ? "1 new tribute needs your review" : sheetCounts.pending + " new tributes need your review"}
            description="Open the Google Sheet to approve or reject new submissions."
            actionLabel="Open Google Sheet"
            actionUrl={sheetUrl}
            color="#E65100"
            bgColor="#FFF3E0"
            borderColor="#FFB74D"
          />
        )}

        {hasUnsyncedApproved && !syncResult && (
          <ActionCard
            icon="publish"
            title={snapshots.length === 0
              ? sheetCounts.approved + " approved tributes are ready to sync"
              : (sheetCounts.approved - latestSnapshotCount) > 0
                ? (sheetCounts.approved - latestSnapshotCount) + " new approved tribute" + ((sheetCounts.approved - latestSnapshotCount) !== 1 ? "s" : "") + " since last sync"
                : "Approved tributes may have changed since last sync"}
            description={'Click "Sync from Google Sheets" below to pull the latest data.'}
            color="#F09000"
            bgColor="#FFF8E1"
            borderColor={BRAND.gold}
          />
        )}

        {statsError && (
          <div style={{ padding: "12px 16px", background: "#FFF3F3", borderRadius: "10px", border: "1px solid #FFD4D4", marginBottom: "16px" }}>
            <p style={{ fontSize: "13px", color: "#D32F2F", margin: 0 }}>{statsError}</p>
          </div>
        )}

        {/* Sync + Publish Section */}
        <div style={{
          background: "#fff", borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "16px",
          overflow: "hidden",
        }}>
          {/* Sync header */}
          <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #F0F0F0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
              <h3 style={{ fontSize: "15px", fontWeight: 700, color: BRAND.text, margin: 0 }}>
                Publish Tributes
              </h3>
              <button
                onClick={handleSync}
                disabled={syncing}
                style={{
                  background: syncing ? "#EEE" : BRAND.primary,
                  color: syncing ? "#999" : "#fff",
                  border: "none", borderRadius: "8px",
                  padding: "10px 20px", fontSize: "13px", fontWeight: 600,
                  cursor: syncing ? "default" : "pointer",
                  display: "flex", alignItems: "center", gap: "6px",
                  transition: "all 0.2s",
                }}
              >
                {syncing && (
                  <span style={{
                    display: "inline-block", width: "12px", height: "12px",
                    border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                    borderRadius: "50%", animation: "spin 0.6s linear infinite",
                  }} />
                )}
                {syncing ? "Syncing..." : "Sync from Google Sheets"}
              </button>
            </div>

            <p style={{ fontSize: "12px", color: BRAND.textLight, margin: 0, lineHeight: 1.5 }}>
              Pull the latest approved tributes from the Google Sheet. If data has changed, a new snapshot will be created for you to preview and publish.
            </p>

            {/* Sync result */}
            {syncResult && !syncResult.error && (
              <div style={{
                marginTop: "12px", padding: "10px 14px", borderRadius: "8px",
                background: syncResult.changed ? "#E8F5E9" : "#F5F5F5",
                border: "1px solid " + (syncResult.changed ? "#A5D6A7" : "#E0E0E0"),
              }}>
                <p style={{ fontSize: "13px", color: syncResult.changed ? "#2E7D32" : "#666", margin: 0, fontWeight: 500 }}>
                  {syncResult.changed
                    ? "New snapshot created with " + syncResult.tributeCount + " tributes"
                    : syncResult.message || "No changes since last sync"}
                </p>
              </div>
            )}

            {syncResult && syncResult.error && (
              <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "8px", background: "#FFF3F3", border: "1px solid #FFD4D4" }}>
                <p style={{ fontSize: "13px", color: "#D32F2F", margin: 0 }}>{syncResult.error}</p>
              </div>
            )}

            {publishResult && publishResult.error && (
              <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "8px", background: "#FFF3F3", border: "1px solid #FFD4D4" }}>
                <p style={{ fontSize: "13px", color: "#D32F2F", margin: 0 }}>{publishResult.error}</p>
              </div>
            )}

            {publishResult && publishResult.success && (
              <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "8px", background: "#E8F5E9", border: "1px solid #A5D6A7" }}>
                <p style={{ fontSize: "13px", color: "#2E7D32", margin: 0, fontWeight: 500 }}>
                  Published! The tribute wall is now showing this version.
                </p>
              </div>
            )}
          </div>

          {/* Snapshot history */}
          {manifestLoading && (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <span style={{
                display: "inline-block", width: "16px", height: "16px",
                border: "2px solid #DDD", borderTopColor: "#999",
                borderRadius: "50%", animation: "spin 0.6s linear infinite",
              }} />
            </div>
          )}

          {!manifestLoading && snapshots.length === 0 && (
            <div style={{ padding: "24px 20px", textAlign: "center" }}>
              <p style={{ fontSize: "13px", color: BRAND.textLight, margin: 0 }}>
                No snapshots yet. Click "Sync from Google Sheets" to create the first one.
              </p>
            </div>
          )}

          {!manifestLoading && snapshots.length > 0 && (
            <div>
              {snapshots.map(function (snap, i) {
                var isLive = snap.file === liveFile;
                var isPublishing = publishing === snap.file;
                return (
                  <div key={snap.file} style={{
                    padding: "14px 20px",
                    borderBottom: i < snapshots.length - 1 ? "1px solid #F5F5F5" : "none",
                    background: isLive ? "#F8FFF8" : "transparent",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                          <span style={{ fontSize: "14px", fontWeight: 600, color: BRAND.text }}>
                            {formatTimestamp(snap.file)}
                          </span>
                          {isLive && (
                            <span style={{
                              fontSize: "10px", fontWeight: 700, color: "#2E7D32",
                              background: "#C8E6C9", padding: "2px 8px", borderRadius: "10px",
                              textTransform: "uppercase", letterSpacing: "0.5px",
                            }}>
                              Live
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "12px", color: BRAND.textLight }}>
                          {snap.tributeCount || (snap.counts && snap.counts.approved) || "?"} tributes
                        </span>
                      </div>

                      <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                        <button
                          onClick={function () { window.open("/?preview=" + encodeURIComponent(snap.file) + "&password=" + encodeURIComponent(password), "_blank"); }}
                          style={{
                            background: "none", border: "1.5px solid #DDD", borderRadius: "6px",
                            padding: "6px 14px", fontSize: "12px", fontWeight: 600,
                            color: BRAND.text, cursor: "pointer", fontFamily: "inherit",
                          }}
                        >
                          Preview
                        </button>

                        {!isLive && (
                          <button
                            onClick={function () { handlePublish(snap.file); }}
                            disabled={isPublishing}
                            style={{
                              background: isPublishing ? "#DDD" : BRAND.gold,
                              color: "#fff", border: "none", borderRadius: "6px",
                              padding: "6px 14px", fontSize: "12px", fontWeight: 600,
                              cursor: isPublishing ? "default" : "pointer",
                              fontFamily: "inherit",
                            }}
                          >
                            {isPublishing ? "..." : "Publish"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer info */}
        {liveFile && manifest && manifest.livePublishedAt && (
          <p style={{ fontSize: "11px", color: "#999", textAlign: "center", margin: "0 0 16px" }}>
            Tribute wall last published {formatDate(manifest.livePublishedAt)}
          </p>
        )}
      </div>
    </div>
  );
}

/* Helper Components */

function StatCircle({ value, label, color, bgColor, highlight }) {
  return (
    <div style={{ textAlign: "center", flex: "1 1 0", maxWidth: "120px" }}>
      <div style={{
        width: "72px", height: "72px", borderRadius: "50%",
        background: bgColor, display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 8px",
        border: highlight ? "2.5px solid " + color : "2px solid " + bgColor,
        boxShadow: highlight ? "0 0 0 4px " + color + "20" : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "all 0.3s ease",
      }}>
        <span style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "28px", fontWeight: 800, color: color,
        }}>
          {value}
        </span>
      </div>
      <span style={{ fontSize: "11px", color: BRAND.textLight, fontWeight: 500 }}>{label}</span>
    </div>
  );
}

function ActionCard({ icon, title, description, actionLabel, actionUrl, color, bgColor, borderColor, children }) {
  var iconSvg;
  if (icon === "review") {
    iconSvg = (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  } else {
    iconSvg = (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    );
  }

  return (
    <div style={{
      background: bgColor, border: "1px solid " + borderColor, borderRadius: "12px",
      padding: "20px", marginBottom: "16px",
    }}>
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <div style={{
          width: "36px", height: "36px", borderRadius: "50%",
          background: color + "15", display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {iconSvg}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: "15px", fontWeight: 700, color: BRAND.text, margin: "0 0 4px" }}>{title}</h3>
          <p style={{ fontSize: "13px", color: BRAND.textLight, margin: 0, lineHeight: 1.5 }}>{description}</p>
        </div>
        {actionUrl && (
          <a href={actionUrl}
            {...(actionUrl.startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
            style={{
              padding: "10px 22px", borderRadius: "8px",
              background: color, color: "#fff", textDecoration: "none",
              fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
              boxShadow: "0 2px 6px " + color + "30",
            }}>
            {actionLabel}
          </a>
        )}
        {children && (
          <div style={{ flexShrink: 0 }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
